import type { DirectoryFilters, FilterArrayKey } from '@/components/client/directory/use-directory-reducer'

import type { DirectoryRelease } from './aggregate-releases'
import type { FilterOptions } from './build-filter-options'
import { filterReleases } from './present-releases'

export type FacetAxis = FilterArrayKey | 'brand'
export type FacetCounts = Record<FacetAxis, ReadonlyMap<string, number>>

// Axes where a product carries one value and the filter chooses among them
// (Type, Style) or takes a single pick (Best for, Hearing loss, How you buy):
// a value's count is what selecting it *instead of* the axis's current pick
// would show. The other axes — features and accessories — are requirements
// that stack (a product must carry every selected value), so a value's count
// is what *adding* it would show. Mirrors productMatchesFilters.
const CHOICE_AXES: ReadonlySet<FacetAxis> = new Set(['classification', 'style', 'tags', 'loss', 'channels', 'brand'])

const OPTION_AXES: ReadonlyArray<[FacetAxis, keyof FilterOptions]> = [
  ['classification', 'classification'],
  ['tags', 'tags'],
  ['channels', 'channels'],
  ['accessories', 'accessories'],
  ['hardware', 'hardware'],
  ['software', 'software'],
  ['loss', 'loss'],
  ['style', 'style'],
  ['brand', 'brands'],
]

const withValue = (filters: DirectoryFilters, axis: FacetAxis, value: string): DirectoryFilters => {
  if (axis === 'brand') {
    return { ...filters, brand: value }
  }
  if (CHOICE_AXES.has(axis)) {
    return { ...filters, [axis]: [value] }
  }
  const current = filters[axis]
  return current.includes(value) ? filters : { ...filters, [axis]: [...current, value] }
}

// How many families each filter option would leave on screen under the
// filters and search term as they stand. Runs the list's own filterReleases
// once per option — a few thousand product checks, milliseconds — so the
// rail's counts are always the truth and a zero means "nothing to see".
export const countFacets = (
  releases: DirectoryRelease[],
  filterOptions: FilterOptions,
  filters: DirectoryFilters,
  term: string
): FacetCounts => {
  const counts: Partial<Record<FacetAxis, Map<string, number>>> = {}
  for (const [axis, optionKey] of OPTION_AXES) {
    const axisCounts = new Map<string, number>()
    for (const option of filterOptions[optionKey]) {
      axisCounts.set(option.value, filterReleases(releases, withValue(filters, axis, option.value), term).length)
    }
    counts[axis] = axisCounts
  }
  return counts as FacetCounts
}
